import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Clock, Smartphone, UserCheck, XCircle } from "lucide-react";
import type { ReactNode } from "react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import type { CreatePaymentRequest, PaymentRail } from "@tilt/pos-sdk";

import { getPosClient } from "../../lib/api";
import { buildPaymentRequest } from "../../lib/payment-method-payloads";
import { formatCents, parseAmountToCents, sanitizeAmount } from "../../lib/format";
import {
  lookupMethodByFeaturePath,
  resolveMethodIcon,
  resolveMethodLabel,
} from "../../lib/payment-methods";
import { useDarkMode } from "../../lib/use-dark-mode";
import { cn } from "../../lib/utils";
import { Button } from "../ui/Button";
import { Field, TextInput } from "../ui/Field";
import { VirtualTerminalPanel } from "./VirtualTerminalPanel";

export type PaymentOutcome =
  | { kind: "success"; amountCents: number }
  | { kind: "pending"; amountCents: number }
  | { kind: "error"; amountCents: number; message?: string };

export interface MethodPickerProps {
  readonly remainingCents: number;
  readonly currency: string;
  readonly submitting: boolean;
  readonly onSubmit: (body: CreatePaymentRequest) => void;
  readonly locationId?: string | null;
  readonly initialCustomerPhone?: string | null;
  readonly initialCustomerName?: string | null;
  /** When non-null, shows outcome flash. Success auto-closes; pending/error require OK. */
  readonly lastPaymentOutcome?: PaymentOutcome | null;
  /** Called when the focused panel opens/closes so the parent can lock adjacent sections. */
  readonly onFormActiveChange?: (active: boolean) => void;
  /** Hide the "How is the customer paying?" heading - use when parent already provides a section header. */
  readonly showHeading?: boolean;
}

export function MethodPicker({
  remainingCents,
  currency,
  submitting,
  onSubmit,
  locationId = null,
  initialCustomerPhone = null,
  initialCustomerName = null,
  lastPaymentOutcome = null,
  onFormActiveChange,
  showHeading = true,
}: MethodPickerProps) {
  const { t } = useTranslation();
  const isDark = useDarkMode();
  const [selectedFeaturePath, setSelectedFeaturePath] = useState<string | null>(null);
  const [cardGroupInProgress, setCardGroupInProgress] = useState<string | null>(null);
  type CardChoiceStep = "present-prompt" | "choose-remote" | null;
  const [cardChoiceStep, setCardChoiceStep] = useState<CardChoiceStep>(null);
  const [pbtPhone, setPbtPhone] = useState<string>("");
  const [amount, setAmount] = useState("");
  const [checkNumber, setCheckNumber] = useState("");
  const [selectedDeviceRef, setSelectedDeviceRef] = useState<string | null>(null);
  const [selectedVirtualDeviceRef, setSelectedVirtualDeviceRef] = useState<string | null>(null);
  const virtualIdempotencyKeyRef = useRef<string>(crypto.randomUUID());

  const railsQuery = useQuery({
    enabled: locationId != null,
    queryKey: ["payment-rails", locationId],
    queryFn: () => getPosClient().getLocationPaymentRails(locationId!),
    staleTime: 5 * 60_000,
  });
  const rails = useMemo(() => railsQuery.data?.rails ?? [], [railsQuery.data]);

  const railsErrored = locationId != null && railsQuery.isError;
  const railsResolved = railsQuery.isSuccess;

  const selectedRail = selectedFeaturePath
    ? lookupMethodByFeaturePath(rails, selectedFeaturePath)
    : null;

  // Card group sub-rails (used for the present-prompt / choose-remote steps)
  const cardGroupRails = useMemo(() => rails.filter((r) => r.card_group_key === "card"), [rails]);
  const cardPresentRail = cardGroupRails.find((r) => r.flow_kind === "card_terminal") ?? null;
  const payByTextRail = cardGroupRails.find((r) => r.flow_kind === "phone_link") ?? null;
  const virtualRail = cardGroupRails.find((r) => r.flow_kind === "embedded_iframe") ?? null;
  const remoteBranchAvailable = !!(payByTextRail || virtualRail);

  // Compute tiles to render: one tile per direct rail + one tile per unique card_group_key
  const tilesToRender = useMemo(() => {
    type Tile =
      | { kind: "direct"; rail: PaymentRail }
      | { kind: "group"; groupKey: string; representative: PaymentRail };
    const result: Tile[] = [];
    const seenGroups = new Set<string>();
    for (const rail of rails) {
      if (rail.card_group_key) {
        if (!seenGroups.has(rail.card_group_key)) {
          seenGroups.add(rail.card_group_key);
          result.push({ kind: "group", groupKey: rail.card_group_key, representative: rail });
        }
      } else {
        result.push({ kind: "direct", rail });
      }
    }
    return result;
  }, [rails]);

  useEffect(() => {
    if (!railsResolved && !railsErrored) return;
    if (selectedFeaturePath == null) return;
    if (railsErrored || !rails.some((r) => r.feature_path === selectedFeaturePath)) {
      setSelectedFeaturePath(null);
      setSelectedDeviceRef(null);
    }
  }, [selectedFeaturePath, railsResolved, railsErrored, rails]);

  const devicePrefs = useQuery({
    enabled: locationId != null && selectedRail?.flow_kind === "card_terminal",
    queryKey: ["device-preferences"],
    queryFn: () => getPosClient().getDevicePreferences(),
    staleTime: 60_000,
  });

  const virtualDevicesQuery = useQuery({
    enabled:
      locationId != null &&
      (selectedRail?.flow_kind === "phone_link" || selectedRail?.flow_kind === "embedded_iframe"),
    queryKey: ["virtual-devices", locationId],
    queryFn: () => getPosClient().listVirtualDevices(locationId!),
    staleTime: 5 * 60_000,
  });
  const virtualDevices = useMemo(
    () => virtualDevicesQuery.data?.devices ?? [],
    [virtualDevicesQuery.data],
  );

  useEffect(() => {
    if (selectedRail?.flow_kind !== "phone_link" && selectedRail?.flow_kind !== "embedded_iframe")
      return;
    if (virtualDevices.length === 0) return;
    setSelectedVirtualDeviceRef((prev) => {
      if (prev && virtualDevices.some((d) => d.device_ref === prev)) return prev;
      return virtualDevices[0].device_ref;
    });
  }, [selectedRail, virtualDevices]);

  const locationDevices =
    devicePrefs.data?.items.find((it) => it.location_id === locationId) ?? null;
  const availableDevices = useMemo(
    () => locationDevices?.available_devices ?? [],
    [locationDevices],
  );
  const savedDefaultRef = locationDevices?.current_default_device_ref ?? null;

  useEffect(() => {
    if (selectedRail?.flow_kind !== "card_terminal") return;
    if (selectedDeviceRef != null) return;
    if (availableDevices.length === 0) return;
    const initial =
      savedDefaultRef && availableDevices.some((d) => d.device_ref === savedDefaultRef)
        ? savedDefaultRef
        : (availableDevices[0]?.device_ref ?? null);
    setSelectedDeviceRef(initial);
  }, [selectedRail, selectedDeviceRef, savedDefaultRef, availableDevices]);

  // Success auto-closes after 3 s. Pending/error stay open until OK is clicked.
  useEffect(() => {
    if (lastPaymentOutcome?.kind !== "success") return;
    const timer = setTimeout(() => resetSelection(), 3000);
    return () => clearTimeout(timer);
  }, [lastPaymentOutcome]);

  const prevRemainingRef = useRef(remainingCents);
  useEffect(() => {
    const prev = prevRemainingRef.current;
    prevRemainingRef.current = remainingCents;
    if (selectedFeaturePath === null) return;
    if (remainingCents === prev) return;
    const prevFormatted = (prev / 100).toFixed(2);
    if (amount === prevFormatted) {
      setAmount((remainingCents / 100).toFixed(2));
    }
  }, [remainingCents, selectedFeaturePath, amount]);

  const isFormActive = selectedFeaturePath !== null || cardGroupInProgress !== null;

  useEffect(() => {
    onFormActiveChange?.(isFormActive);
  }, [isFormActive, onFormActiveChange]);

  if (remainingCents === 0) return null;

  const amountCents = parseAmountToCents(amount);
  // Only amount_only + is_voidable rails (i.e. cash) allow the cashier to tender more than the
  // remaining balance so they can compute change due. ACH is also amount_only but is_voidable=false,
  // so it is correctly excluded. Check has is_voidable=true but flow_kind="check_number", also excluded.
  const isCash = selectedRail?.flow_kind === "amount_only" && selectedRail?.is_voidable === true;
  const amountInvalid =
    amountCents == null ||
    amountCents <= 0 ||
    (!isCash && selectedRail !== null && amountCents > remainingCents);
  const checkNumberInvalid = (selectedRail?.requires_check_number ?? false) && !checkNumber.trim();

  const appliedCents =
    amountCents == null ? null : isCash ? Math.min(amountCents, remainingCents) : amountCents;
  const changeCents =
    isCash && amountCents != null && amountCents > remainingCents
      ? amountCents - remainingCents
      : 0;
  const isPartial = appliedCents != null && appliedCents > 0 && appliedCents < remainingCents;
  const remainingAfter = appliedCents != null ? remainingCents - appliedCents : remainingCents;

  const activePanelLabel = (() => {
    if (cardGroupInProgress !== null) {
      const rep = rails.find((r) => r.card_group_key === cardGroupInProgress);
      return rep ? resolveMethodLabel(rep, t) : cardGroupInProgress;
    }
    if (selectedRail) return resolveMethodLabel(selectedRail, t);
    return "";
  })();

  function resetSelection() {
    setSelectedFeaturePath(null);
    setCardGroupInProgress(null);
    setCardChoiceStep(null);
    setAmount("");
    setCheckNumber("");
    setSelectedDeviceRef(null);
  }

  function pickRail(featurePath: string) {
    setSelectedFeaturePath(featurePath);
    setCardGroupInProgress(null);
    setCardChoiceStep(null);
    setAmount((remainingCents / 100).toFixed(2));
    setCheckNumber("");
    setSelectedDeviceRef(null);
    const rail = lookupMethodByFeaturePath(rails, featurePath);
    if (rail?.flow_kind === "phone_link") {
      setPbtPhone(looksLikeUsablePhone(initialCustomerPhone) ? initialCustomerPhone! : "");
    }
  }

  function pickGroupTile(groupKey: string) {
    setCardGroupInProgress(groupKey);
    setCardChoiceStep("present-prompt");
  }

  function pickDirectTile(featurePath: string) {
    pickRail(featurePath);
  }

  function answerCustomerPresent() {
    if (cardPresentRail) pickRail(cardPresentRail.feature_path);
  }
  function answerCustomerRemote() {
    setCardChoiceStep("choose-remote");
  }
  function chooseTextToPay() {
    if (payByTextRail) pickRail(payByTextRail.feature_path);
  }
  function chooseVirtualTerminal() {
    if (virtualRail) pickRail(virtualRail.feature_path);
  }

  function getDeviceId(): string | undefined {
    if (!selectedRail?.requires_device_picker) return undefined;
    if (selectedRail.flow_kind === "card_terminal") return selectedDeviceRef ?? undefined;
    if (selectedRail.flow_kind === "phone_link") return selectedVirtualDeviceRef ?? undefined;
    return undefined;
  }

  function submit() {
    if (appliedCents == null || appliedCents <= 0) return;
    if (!selectedRail) return;
    if (selectedRail.flow_kind === "embedded_iframe") return;
    const req = buildPaymentRequest(selectedRail, appliedCents, {
      checkNumber: checkNumber.trim() || undefined,
      customerPhone: pbtPhone.trim() || undefined,
      deviceId: getDeviceId(),
      tenderedCents: isCash ? (amountCents ?? undefined) : undefined,
    });
    onSubmit(req);
  }

  return (
    <div className="space-y-3">
      {showHeading && (
        <h2
          className={cn(
            "text-sm font-semibold transition-colors duration-200",
            isFormActive ? "text-muted" : "text-foreground",
          )}
        >
          {t("sale.howPaying")}
        </h2>
      )}

      {/* ── Tile grid ──
          When the focused panel is active: non-selected tiles dim to 30%
          and become non-interactive, but the selected tile keeps its full
          highlight so the cashier always sees which method is in progress. */}
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
        {tilesToRender.map((tile) => {
          if (tile.kind === "direct") {
            const tileSelected = selectedFeaturePath === tile.rail.feature_path;
            const dimmed = isFormActive && !tileSelected;
            const resolvedIcon = resolveMethodIcon(tile.rail, isDark);
            return (
              <MethodTile
                key={tile.rail.feature_path}
                label={resolveMethodLabel(tile.rail, t)}
                icon={
                  typeof resolvedIcon === "string" ? (
                    <img src={resolvedIcon} className="h-5 w-5" alt="" aria-hidden={true} />
                  ) : (
                    (() => {
                      const Icon = resolvedIcon;
                      return <Icon className="h-5 w-5" />;
                    })()
                  )
                }
                selected={tileSelected}
                available
                dimmed={dimmed}
                comingSoonLabel={t("method.comingSoon")}
                {...(!dimmed && { onClick: () => pickDirectTile(tile.rail.feature_path) })}
              />
            );
          }
          // Group tile
          const groupSelected =
            cardGroupInProgress === tile.groupKey ||
            cardGroupRails.some((r) => r.feature_path === selectedFeaturePath);
          const dimmed = isFormActive && !groupSelected;
          const resolvedGroupIcon = resolveMethodIcon(tile.representative, isDark);
          return (
            <MethodTile
              key={`group:${tile.groupKey}`}
              label={resolveMethodLabel(tile.representative, t)}
              icon={
                typeof resolvedGroupIcon === "string" ? (
                  <img src={resolvedGroupIcon} className="h-5 w-5" alt="" aria-hidden={true} />
                ) : (
                  (() => {
                    const Icon = resolvedGroupIcon;
                    return <Icon className="h-5 w-5" />;
                  })()
                )
              }
              selected={groupSelected}
              available
              dimmed={dimmed}
              comingSoonLabel={t("method.comingSoon")}
              {...(!dimmed && { onClick: () => pickGroupTile(tile.groupKey) })}
            />
          );
        })}
      </div>

      {/* ── Elevated focused panel ── */}
      {isFormActive && (
        <div className="rounded-xl border border-line bg-card shadow-md transition-shadow">
          {/* OUTCOME FLASH - success / pending / error */}
          {lastPaymentOutcome != null && !submitting && (
            <div className="flex flex-col items-center gap-4 px-5 py-8 text-center">
              {lastPaymentOutcome.kind === "success" && (
                <>
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-emerald-100 dark:bg-emerald-900/30">
                    <CheckCircle2 className="h-8 w-8 text-emerald-500 dark:text-emerald-400" />
                  </div>
                  <p className="text-base font-semibold text-strong">
                    {t("sale.paymentAppliedInPanel", {
                      amount: formatCents(lastPaymentOutcome.amountCents, currency),
                    })}
                  </p>
                </>
              )}
              {lastPaymentOutcome.kind === "pending" && (
                <>
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-amber-100 dark:bg-amber-900/30">
                    <Clock className="h-8 w-8 text-amber-500 dark:text-amber-400" />
                  </div>
                  <div>
                    <p className="text-base font-semibold text-strong">
                      {t("sale.paymentPendingInPanel", {
                        amount: formatCents(lastPaymentOutcome.amountCents, currency),
                      })}
                    </p>
                    <p className="mt-1 text-sm text-muted">{t("sale.paymentPendingHint")}</p>
                  </div>
                  <Button variant="primary" size="sm" onClick={resetSelection}>
                    {t("common.ok")}
                  </Button>
                </>
              )}
              {lastPaymentOutcome.kind === "error" && (
                <>
                  <div className="flex h-14 w-14 items-center justify-center rounded-full bg-rose-100 dark:bg-rose-900/30">
                    <XCircle className="h-8 w-8 text-rose-500 dark:text-rose-400" />
                  </div>
                  <div>
                    <p className="text-base font-semibold text-strong">
                      {t("sale.paymentFailedInPanel")}
                    </p>
                    {lastPaymentOutcome.message && (
                      <p className="mt-1 text-sm text-muted">{lastPaymentOutcome.message}</p>
                    )}
                  </div>
                  <Button variant="primary" size="sm" onClick={resetSelection}>
                    {t("common.ok")}
                  </Button>
                </>
              )}
            </div>
          )}

          {/* SPINNER - shown while API call is in flight */}
          {submitting && (
            <div className="flex flex-col items-center gap-3 px-5 py-7">
              <div className="h-8 w-8 animate-spin rounded-full border-[3px] border-brand-primary/20 border-t-brand-primary" />
              <p className="text-sm font-medium text-foreground">{t("sale.processingPayment")}</p>
            </div>
          )}

          {/* ACTIVE FORM */}
          {!submitting && lastPaymentOutcome == null && (
            <>
              {/* Panel header */}
              <div className="flex items-center justify-between border-b border-line px-4 py-2.5">
                <span className="text-xs font-semibold uppercase tracking-wider text-brand-primary">
                  {activePanelLabel}
                </span>
                {cardChoiceStep !== null && (
                  <button
                    type="button"
                    onClick={resetSelection}
                    disabled={submitting}
                    className="rounded-md border border-line px-3 py-1.5 text-xs font-medium text-foreground transition-colors hover:bg-surface-muted disabled:pointer-events-none disabled:opacity-40"
                  >
                    {t("common.cancel")}
                  </button>
                )}
              </div>

              <div className="space-y-4 p-4">
                {/* ── Card-group choice step ── */}
                {cardGroupInProgress !== null && cardChoiceStep !== null && (
                  <>
                    {cardChoiceStep === "present-prompt" && (
                      <>
                        <p className="text-sm font-medium text-foreground">
                          {t("sale.cardChoiceInlineQuestion")}
                        </p>
                        <div className="grid grid-cols-2 gap-3">
                          {cardPresentRail && (
                            <button
                              type="button"
                              onClick={answerCustomerPresent}
                              className="flex h-16 flex-col items-center justify-center gap-1.5 rounded-xl border-2 border-brand-primary bg-brand-primary/5 text-sm font-semibold text-brand-primary transition hover:bg-brand-primary/10 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                            >
                              <UserCheck className="h-5 w-5" aria-hidden={true} />
                              {t("sale.cardChoicePresent")}
                            </button>
                          )}
                          {remoteBranchAvailable && (
                            <button
                              type="button"
                              onClick={answerCustomerRemote}
                              className="flex h-16 flex-col items-center justify-center gap-1.5 rounded-xl border border-line bg-surface text-sm font-medium text-foreground transition hover:border-line-strong hover:bg-surface-muted focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary"
                            >
                              <Smartphone className="h-5 w-5" aria-hidden={true} />
                              {t("sale.cardChoiceRemote")}
                            </button>
                          )}
                        </div>
                      </>
                    )}
                    {cardChoiceStep === "choose-remote" && (
                      <>
                        <p className="text-sm text-muted">{t("sale.cardChoiceRemoteSubtitle")}</p>
                        <div className="grid grid-cols-2 gap-3">
                          {payByTextRail &&
                            (() => {
                              const pbtResolved = resolveMethodIcon(payByTextRail, isDark);
                              return (
                                <ChannelTile
                                  icon={
                                    typeof pbtResolved === "string" ? (
                                      <img
                                        src={pbtResolved}
                                        className="h-5 w-5"
                                        alt=""
                                        aria-hidden={true}
                                      />
                                    ) : (
                                      (() => {
                                        const I = pbtResolved;
                                        return (
                                          <I
                                            className="h-5 w-5 text-brand-primary"
                                            aria-hidden={true}
                                          />
                                        );
                                      })()
                                    )
                                  }
                                  label={t("sale.cardChoiceTextToPay")}
                                  onClick={chooseTextToPay}
                                  available
                                />
                              );
                            })()}
                          {virtualRail &&
                            (() => {
                              const virtResolved = resolveMethodIcon(virtualRail, isDark);
                              return (
                                <ChannelTile
                                  icon={
                                    typeof virtResolved === "string" ? (
                                      <img
                                        src={virtResolved}
                                        className="h-5 w-5"
                                        alt=""
                                        aria-hidden={true}
                                      />
                                    ) : (
                                      (() => {
                                        const I = virtResolved;
                                        return (
                                          <I
                                            className="h-5 w-5 text-brand-primary"
                                            aria-hidden={true}
                                          />
                                        );
                                      })()
                                    )
                                  }
                                  label={t("sale.cardChoiceVirtualTerminal")}
                                  onClick={chooseVirtualTerminal}
                                  available
                                  unavailableLabel={t("method.comingSoon")}
                                />
                              );
                            })()}
                        </div>
                      </>
                    )}
                  </>
                )}

                {/* ── Method-specific form ── */}
                {selectedRail !== null && (
                  <>
                    {/* Amount input — shown for all methods so cashiers can
                        tender a partial amount (e.g. split tender across two
                        virtual-terminal transactions). */}
                    <Field
                      label={isCash ? t("sale.tendered") : t("sale.amount")}
                      hint={
                        isCash
                          ? t("sale.tenderedHint")
                          : t("sale.amountHint", {
                              remaining: formatCents(remainingCents, currency),
                            })
                      }
                      error={
                        amountInvalid
                          ? isCash
                            ? t("sale.amountInvalidCash")
                            : t("sale.amountInvalidPartial", {
                                remaining: formatCents(remainingCents, currency),
                              })
                          : null
                      }
                    >
                      <TextInput
                        type="text"
                        inputMode="decimal"
                        value={amount}
                        onChange={(e) => setAmount(sanitizeAmount(e.target.value))}
                        placeholder={(remainingCents / 100).toFixed(2)}
                        invalid={amountInvalid}
                        autoFocus
                        className="text-right text-lg tabular-nums"
                      />
                    </Field>

                    {!amountInvalid && isPartial && (
                      <div className="flex items-center justify-between rounded-md bg-surface-muted px-3 py-2 text-sm ring-1 ring-inset ring-line">
                        <span className="text-muted">
                          {t("sale.amountPartial", {
                            remaining: formatCents(remainingAfter, currency),
                          })}
                        </span>
                      </div>
                    )}

                    {isCash && changeCents > 0 && (
                      <div className="flex items-center justify-between rounded-md bg-surface-muted px-3 py-2 text-sm ring-1 ring-inset ring-line">
                        <span className="text-muted">{t("sale.changeDue")}</span>
                        <span className="font-mono text-base font-semibold tabular-nums text-emerald-600 dark:text-emerald-400">
                          {formatCents(changeCents, currency)}
                        </span>
                      </div>
                    )}

                    {selectedRail.flow_kind !== "embedded_iframe" && (
                      <>
                        {selectedRail.requires_check_number && (
                          <Field
                            label={t("sale.checkNumber")}
                            hint={t("sale.checkPendingNote")}
                            error={checkNumberInvalid ? t("sale.checkNumberRequired") : null}
                          >
                            <TextInput
                              type="text"
                              value={checkNumber}
                              onChange={(e) => setCheckNumber(e.target.value)}
                              maxLength={64}
                              placeholder={t("sale.checkNumberPlaceholder")}
                              invalid={checkNumberInvalid}
                            />
                          </Field>
                        )}

                        {selectedRail.flow_kind === "card_terminal" && (
                          <Field
                            label={t("sale.deviceLabel")}
                            hint={
                              savedDefaultRef
                                ? t("sale.deviceHintWithDefault")
                                : t("sale.deviceHintNoDefault")
                            }
                          >
                            {devicePrefs.isPending ? (
                              <div className="rounded-md bg-surface-muted px-3 py-2 text-sm text-muted">
                                {t("sale.deviceLoading")}
                              </div>
                            ) : availableDevices.length === 0 ? (
                              <div className="rounded-md bg-amber-50 px-3 py-2 text-sm text-amber-900 ring-1 ring-inset ring-amber-200 dark:bg-amber-950 dark:text-amber-200 dark:ring-amber-900">
                                {t("sale.deviceNoneAvailable")}
                              </div>
                            ) : (
                              <select
                                value={selectedDeviceRef ?? ""}
                                onChange={(e) => setSelectedDeviceRef(e.target.value || null)}
                                className="block w-full rounded-md bg-surface px-3 py-2 text-sm text-foreground ring-1 ring-inset ring-line focus:outline-none focus:ring-2 focus:ring-brand-primary"
                              >
                                {availableDevices.map((d) => (
                                  <option key={d.device_ref} value={d.device_ref}>
                                    {d.label || d.device_ref}
                                    {d.device_ref === savedDefaultRef
                                      ? ` ${t("sale.deviceDefaultMarker")}`
                                      : ""}
                                  </option>
                                ))}
                              </select>
                            )}
                          </Field>
                        )}

                        {selectedRail.flow_kind === "phone_link" && (
                          <>
                            <Field
                              label={t("sale.smsPhoneLabel")}
                              hint={
                                looksLikeUsablePhone(initialCustomerPhone)
                                  ? t("sale.smsPhoneHintFromOrder")
                                  : t("sale.smsPhoneHintNoOrder")
                              }
                            >
                              <TextInput
                                type="tel"
                                inputMode="tel"
                                value={pbtPhone}
                                onChange={(e) => setPbtPhone(e.target.value)}
                                placeholder="+15555550100"
                                autoComplete="tel"
                              />
                            </Field>
                            {selectedRail.requires_device_picker && virtualDevices.length >= 2 && (
                              <Field label={t("sale.virtual.devicePicker")}>
                                <select
                                  value={selectedVirtualDeviceRef ?? ""}
                                  onChange={(e) =>
                                    setSelectedVirtualDeviceRef(e.target.value || null)
                                  }
                                  className="w-full rounded-md border border-line bg-surface px-2 py-1.5 text-sm text-foreground focus:outline-none focus:ring-2 focus:ring-brand-primary"
                                >
                                  {virtualDevices.map((d) => (
                                    <option key={d.device_ref} value={d.device_ref}>
                                      {d.label ? `${d.label} (${d.device_ref})` : d.device_ref}
                                    </option>
                                  ))}
                                </select>
                              </Field>
                            )}
                          </>
                        )}

                        <div className="flex items-center justify-between pt-1">
                          <Button
                            variant="ghost"
                            size="md"
                            onClick={resetSelection}
                            disabled={submitting}
                          >
                            {t("common.cancel")}
                          </Button>
                          <Button
                            variant="primary"
                            size="md"
                            onClick={submit}
                            loading={submitting}
                            disabled={amountInvalid || checkNumberInvalid}
                          >
                            {t("sale.confirmAmount", {
                              amount: formatCents(appliedCents ?? remainingCents, currency),
                            })}
                          </Button>
                        </div>
                      </>
                    )}

                    {selectedRail.flow_kind === "embedded_iframe" &&
                      !amountInvalid &&
                      locationId != null && (
                        <VirtualTerminalPanel
                          locationId={locationId}
                          amountCents={appliedCents ?? remainingCents}
                          currency={currency}
                          idempotencyKey={virtualIdempotencyKeyRef.current}
                          {...(initialCustomerPhone
                            ? { customerPhoneDefault: initialCustomerPhone }
                            : {})}
                          {...(initialCustomerName
                            ? { cardholderNameDefault: initialCustomerName }
                            : {})}
                          submitting={submitting}
                          onSubmit={onSubmit}
                          onCancel={() => {
                            virtualIdempotencyKeyRef.current = crypto.randomUUID();
                            setSelectedFeaturePath(null);
                            setCardGroupInProgress(null);
                          }}
                        />
                      )}
                  </>
                )}
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
}

function looksLikeUsablePhone(s: string | null | undefined): boolean {
  if (!s) return false;
  return s.replace(/\D/g, "").length >= 10;
}

function MethodTile({
  label,
  icon,
  selected,
  available,
  dimmed = false,
  comingSoonLabel,
  onClick,
}: {
  readonly label: string;
  readonly icon: ReactNode;
  readonly selected?: boolean;
  readonly available: boolean;
  readonly dimmed?: boolean;
  readonly comingSoonLabel: string;
  readonly onClick?: () => void;
}) {
  return (
    <button
      type="button"
      disabled={!available || dimmed}
      aria-disabled={!available || dimmed}
      onClick={onClick}
      className={cn(
        "group relative flex h-14 flex-col items-center justify-center gap-1 rounded-xl border bg-surface text-sm font-medium transition-all duration-200",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary",
        dimmed && "pointer-events-none opacity-30",
        !dimmed &&
          available &&
          (selected
            ? "border-brand-primary bg-brand-primary/10 text-brand-primary shadow-sm"
            : "border-brand-primary/25 bg-brand-primary/5 text-foreground hover:border-brand-primary/50 hover:bg-brand-primary/10"),
        !dimmed &&
          !available &&
          "cursor-not-allowed border-dashed border-line-strong bg-surface-muted text-muted opacity-80",
      )}
    >
      <span
        className={cn(
          "transition-colors",
          selected && available && !dimmed && "text-brand-primary",
          !available && "opacity-60",
        )}
        aria-hidden="true"
      >
        {icon}
      </span>
      <span className={cn(!available && "opacity-80")}>{label}</span>
      {!available && (
        <span className="absolute right-2 top-2 rounded-full bg-amber-100 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wider text-amber-800 shadow-sm dark:bg-amber-900/30 dark:text-amber-200 dark:shadow-none">
          {comingSoonLabel}
        </span>
      )}
    </button>
  );
}

function ChannelTile({
  icon,
  label,
  onClick,
  available,
  unavailableLabel,
}: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly onClick: () => void;
  readonly available: boolean;
  readonly unavailableLabel?: string | undefined;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      disabled={!available}
      className={cn(
        "group relative flex h-16 w-full flex-col items-center justify-center gap-1.5 overflow-hidden rounded-xl border transition",
        "focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-primary",
        available
          ? "border-line bg-surface text-foreground hover:border-brand-primary hover:bg-brand-primary/5"
          : "cursor-not-allowed border-dashed border-line-strong bg-surface-muted text-faint",
      )}
    >
      <span className={cn("transition-transform", available && "group-hover:-translate-y-0.5")}>
        {icon}
      </span>
      <span className="text-sm font-medium">{label}</span>
      {!available && unavailableLabel && (
        <span className="absolute right-2 top-2 rounded-full bg-amber-100 px-2 py-0.5 text-[0.625rem] font-semibold uppercase tracking-wider text-amber-800 dark:bg-amber-900/30 dark:text-amber-200">
          {unavailableLabel}
        </span>
      )}
    </button>
  );
}
